import { useState } from 'react';

import { Button } from './Button';

/**
 * v0.5 Stage D.1 — Bulk-acknowledge sweeper.
 *
 * Renders at the top of the HITL queue when at least
 * {@link MIN_RECOMMENDED_FOR_SWEEPER} (= 2) recommended-tier cards are
 * open on the run. Single-click "Confirm all" dispatches the batched
 * resolution; "Review individually" dismisses the sweeper for the
 * current session so the reviewer can walk through each card.
 *
 * The component is purely presentational — the parent passes:
 *   * ``recommendedCount`` — count to show in the headline.
 *   * ``onConfirmAll`` — async callback invoked when the reviewer
 *     clicks "Confirm all". The parent owns the actual API call so
 *     this component stays decoupled from the api service for
 *     unit-testing.
 *   * ``onDismiss`` — sync callback invoked when the reviewer clicks
 *     "Review individually". Parent typically uses this to set a
 *     state flag that hides the sweeper.
 *
 * The "Confirm all" button shows pending state during dispatch and
 * disables itself + the "Review individually" button to prevent
 * double-fire. Errors surface inline below the buttons (matches the
 * single-card error pattern in HitRow).
 */

export const MIN_RECOMMENDED_FOR_SWEEPER = 2;

export interface HitlBulkAckSweeperProps {
  /** Number of open recommended-tier cards. The sweeper renders only
   * when this is >= MIN_RECOMMENDED_FOR_SWEEPER. */
  recommendedCount: number;
  /** Async callback when reviewer clicks "Confirm all". Parent owns
   * the api.postBatchConfirm call. */
  onConfirmAll: () => Promise<void>;
  /** Sync callback when reviewer clicks "Review individually" — the
   * parent typically hides the sweeper for this session. */
  onDismiss: () => void;
}

export function HitlBulkAckSweeper({
  recommendedCount,
  onConfirmAll,
  onDismiss,
}: HitlBulkAckSweeperProps) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Defensive: if the parent passes a count below the threshold the
  // sweeper renders nothing. Centralising the gate here means callers
  // don't have to repeat the >= 2 check at every render site.
  if (recommendedCount < MIN_RECOMMENDED_FOR_SWEEPER) {
    return null;
  }

  const handleConfirmAll = async () => {
    setPending(true);
    setError(null);
    try {
      await onConfirmAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk confirm failed');
    } finally {
      setPending(false);
    }
  };

  return (
    <section
      data-testid="hitl-bulk-ack-sweeper"
      aria-label="Bulk-acknowledge recommended HITLs"
      className="rounded-md border border-brand/40 bg-brand/5 p-4 space-y-2"
    >
      <p className="text-sm font-medium">
        <span aria-hidden="true" className="mr-2">✓</span>
        {recommendedCount} recommended-tier card
        {recommendedCount === 1 ? '' : 's'} ready to confirm
      </p>
      <p className="text-xs text-muted">
        Confirm all in one click, or review each card individually.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="primary"
          onClick={handleConfirmAll}
          disabled={pending}
          data-testid="hitl-bulk-ack-confirm-all"
        >
          {pending ? 'Confirming…' : 'Confirm all'}
        </Button>
        <Button
          variant="secondary"
          onClick={onDismiss}
          disabled={pending}
          data-testid="hitl-bulk-ack-review-individually"
        >
          Review individually
        </Button>
      </div>
      {error && (
        <p
          role="alert"
          data-testid="hitl-bulk-ack-error"
          className="text-xs text-danger"
        >
          {error}
        </p>
      )}
    </section>
  );
}
